import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Snackbar,
    Alert,
    CircularProgress,
    Tooltip,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BuildIcon from '@mui/icons-material/Build';
import BrushIcon from '@mui/icons-material/Brush';
import FenceIcon from '@mui/icons-material/Fence';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import HandymanIcon from '@mui/icons-material/Handyman';
import AdminLayout from '../../../components/AdminLayout';
import axios from 'axios';

const backendUrl =
    import.meta.env.MODE === 'development'
        ? `http://${import.meta.env.VITE_BACKEND_URL}`
        : `https://${import.meta.env.VITE_BACKEND_URL_PROD}`;

// Map of available icons
const iconOptions = [
    { value: 'build', label: 'Build', component: <BuildIcon /> },
    { value: 'brush', label: 'Brush', component: <BrushIcon /> },
    { value: 'fence', label: 'Fence', component: <FenceIcon /> },
    { value: 'plumbing', label: 'Plumbing', component: <PlumbingIcon /> },
    {
        value: 'homeRepair',
        label: 'Home Repair',
        component: <HomeRepairServiceIcon />,
    },
    {
        value: 'cleaning',
        label: 'Cleaning',
        component: <CleaningServicesIcon />,
    },
    { value: 'handyman', label: 'Handyman', component: <HandymanIcon /> },
];

// Function to render icon based on string value
const renderIcon = (iconName) => {
    const icon = iconOptions.find((option) => option.value === iconName);
    return icon ? icon.component : <BuildIcon />;
};

const AdminServicesPage = () => {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [currentService, setCurrentService] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        icon: 'build',
    });
    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    useEffect(() => {
        const fetchServices = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${backendUrl}/services`, {
                    headers: {
                        Authorization: token ? `Bearer ${token}` : undefined,
                    },
                });
                setServices(response.data);
            } catch (error) {
                console.error('Error fetching services:', error);
                setSnackbar({
                    open: true,
                    message: 'Failed to load services. Please try again.',
                    severity: 'error',
                });
            } finally {
                setLoading(false);
            }
        };

        fetchServices();
    }, []);

    const handleOpenDialog = (service = null) => {
        if (service) {
            setCurrentService(service);
            setFormData({
                title: service.title,
                description: service.description,
                icon: service.icon,
            });
        } else {
            setCurrentService(null);
            setFormData({
                title: '',
                description: '',
                icon: 'build',
            });
        }
        setErrors({});
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    const handleOpenDeleteDialog = (service) => {
        console.log(service);
        setCurrentService(service);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });

        // Clear error when user types
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: '',
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }

        if (!formData.description.trim()) {
            newErrors.description = 'Description is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            if (currentService) {
                // Update existing service

                const response = await axios.put(
                    `${backendUrl}/services/${currentService._id}`,
                    formData,
                    {
                        headers: {
                            Authorization: token
                                ? `Bearer ${token}`
                                : undefined,
                        },
                    }
                );

                const updatedServices = services.map((service) =>
                    service._id === currentService._id ? response.data : service
                );

                setServices(updatedServices);

                setSnackbar({
                    open: true,
                    message: 'Service updated successfully!',
                    severity: 'success',
                });
            } else {
                // Create new service
                const response = await axios.post(
                    `${backendUrl}/services`,
                    formData,
                    {
                        headers: {
                            Authorization: token
                                ? `Bearer ${token}`
                                : undefined,
                        },
                    }
                );
                const newService = response.data;

                setServices([...services, newService]);

                setSnackbar({
                    open: true,
                    message: 'Service created successfully!',
                    severity: 'success',
                });
            }

            handleCloseDialog();
        } catch (error) {
            console.error('Error saving service:', error);
            setSnackbar({
                open: true,
                message: 'Failed to save service. Please try again.',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!currentService) return;

        setLoading(true);

        try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(
                `${backendUrl}/services/${currentService._id}`,
                {
                    headers: {
                        Authorization: token ? `Bearer ${token}` : undefined,
                    },
                }
            );

            if (response.status === 200) {
                const filteredServices = services.filter(
                    (service) => service._id !== currentService._id
                );
                setServices(filteredServices);

                setSnackbar({
                    open: true,
                    message: 'Service deleted successfully!',
                    severity: 'success',
                });

                handleCloseDeleteDialog();
            }
        } catch (error) {
            console.error('Error deleting service:', error);
            setSnackbar({
                open: true,
                message: 'Failed to delete service. Please try again.',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({
            ...snackbar,
            open: false,
        });
    };

    return (
        <AdminLayout>
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 4,
                    }}
                >
                    <Typography variant="h4" component="h1">
                        Manage Services
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        Add Service
                    </Button>
                </Box>

                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                    <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="5%">Icon</TableCell>
                                    <TableCell width="20%">Title</TableCell>
                                    <TableCell width="60%">
                                        Description
                                    </TableCell>
                                    <TableCell width="15%" align="center">
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading && services.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={4}
                                            align="center"
                                            sx={{ py: 3 }}
                                        >
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : services.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={4}
                                            align="center"
                                            sx={{ py: 3 }}
                                        >
                                            No services found. Add your first
                                            service!
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    services.map((service) => (
                                        <TableRow key={service.id} hover>
                                            <TableCell>
                                                <Box
                                                    sx={{
                                                        color: 'primary.main',
                                                    }}
                                                >
                                                    {renderIcon(service.icon)}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {service.title}
                                            </TableCell>
                                            <TableCell>
                                                {service.description}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() =>
                                                            handleOpenDialog(
                                                                service
                                                            )
                                                        }
                                                        size="small"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        color="error"
                                                        onClick={() =>
                                                            handleOpenDeleteDialog(
                                                                service
                                                            )
                                                        }
                                                        size="small"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Add/Edit Service Dialog */}
                <Dialog
                    open={dialogOpen}
                    onClose={handleCloseDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        {currentService ? 'Edit Service' : 'Add New Service'}
                    </DialogTitle>
                    <DialogContent>
                        <Box component="form" noValidate sx={{ mt: 2 }}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Title"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                error={!!errors.title}
                                helperText={errors.title}
                                required
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Description"
                                name="description"
                                multiline
                                rows={4}
                                value={formData.description}
                                onChange={handleChange}
                                error={!!errors.description}
                                helperText={errors.description}
                                required
                            />
                            <FormControl fullWidth margin="normal">
                                <InputLabel id="icon-select-label">
                                    Icon
                                </InputLabel>
                                <Select
                                    labelId="icon-select-label"
                                    id="icon-select"
                                    name="icon"
                                    value={formData.icon}
                                    onChange={handleChange}
                                    label="Icon"
                                >
                                    {iconOptions.map((option) => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Box sx={{ mr: 1 }}>
                                                    {option.component}
                                                </Box>
                                                {option.label}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Cancel</Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            color="primary"
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Save'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={handleCloseDeleteDialog}
                >
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete the service "
                            {currentService?.title}"? This action cannot be
                            undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDeleteDialog}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDelete}
                            variant="contained"
                            color="error"
                            disabled={loading}
                        >
                            {loading ? (
                                <CircularProgress size={24} />
                            ) : (
                                'Delete'
                            )}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar for notifications */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </AdminLayout>
    );
};

export default AdminServicesPage;
